import { useState, useMemo, useCallback, useEffect } from "react";
import { List, useTable } from "@refinedev/antd";
import { Button, Input, Modal, Select, Space, Table, Tag, Tooltip, Popconfirm, Typography, Image, Badge, Radio, Checkbox, Alert } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { DeleteOutlined, EditOutlined, PlusOutlined, SearchOutlined, FilterOutlined, CheckCircleOutlined, ClockCircleOutlined, SyncOutlined, BookOutlined, PictureOutlined, CloudDownloadOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { useDelete, getDefaultFilter, useApiUrl, useGo } from "@refinedev/core";
import { useTaskTracker } from "@/providers/task-tracker";
import { useNotify } from "@/hooks/useNotify";

const { Text } = Typography;

type KBDocument = {
  id: string;
  title: string;
  document_type: string;
  content: string;
  contextualized_content?: string;
  product_info?: {
    seller_sku?: string;
    wb_sku?: string;
    category?: string;
    color?: string | null;
    barcode?: string;
    photo_url?: string;
    price?: number | string;
    old_price?: number | string;
    characteristics?: Record<string, any>;
    attributes?: Record<string, any>;
  };
  tags: string[];
  metadata: any;
  is_indexed: boolean;
  created_at: string;
  updated_at: string;
};

export default function KBList() {
  const notify = useNotify();
  const go = useGo();
  const { registerReindexJob } = useTaskTracker();
  const {
    tableProps,
    filters,
    setFilters,
    tableQuery,
  } = useTable<KBDocument>({
    resource: "knowledge-base",
    pagination: { mode: "server", pageSize: 20 },
    sorters: { initial: [{ field: "updated_at", order: "desc" }] },
    filters: {
      initial: [
        { field: "title", operator: "contains", value: undefined },
        { field: "document_type", operator: "eq", value: undefined },
        { field: "is_indexed", operator: "eq", value: undefined },
      ],
    },
    syncWithLocation: true,
  });

  const { mutate: deleteDoc } = useDelete();

  const [searchDraft, setSearchDraft] = useState("");
  const [docTypeDraft, setDocTypeDraft] = useState<string | undefined>();
  const [indexedDraft, setIndexedDraft] = useState<boolean | undefined>();

  // Sync modal state
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [syncMode, setSyncMode] = useState<"quick" | "full">("quick");
  const [syncMarketplaces, setSyncMarketplaces] = useState<string[]>(["wildberries", "ozon"]);
  const [syncLoading, setSyncLoading] = useState(false);

  const apiUrl = useApiUrl();

  const appliedSearchValue = useMemo(() => {
    const f = getDefaultFilter("title", filters);
    return typeof f?.value === "string" ? f.value : "";
  }, [filters]);

  const appliedDocTypeValue = useMemo(() => {
    const f = getDefaultFilter("document_type", filters);
    return (f?.value as string | undefined) ?? undefined;
  }, [filters]);

  const appliedIndexedValue = useMemo(() => {
    const f = getDefaultFilter("is_indexed", filters);
    return (f?.value as boolean | undefined) ?? undefined;
  }, [filters]);

  // Синхронизация draft-состояний с примененными фильтрами
  useEffect(() => {
    setSearchDraft(appliedSearchValue);
  }, [appliedSearchValue]);

  useEffect(() => {
    setDocTypeDraft(appliedDocTypeValue);
  }, [appliedDocTypeValue]);

  useEffect(() => {
    setIndexedDraft(appliedIndexedValue);
  }, [appliedIndexedValue]);

  const updateFilters = useCallback(
    (search: string, docType?: string, indexed?: boolean) => {
      const trimmed = search.trim();
      setFilters(
        [
          { field: "title", operator: "contains", value: trimmed || undefined },
          { field: "document_type", operator: "eq", value: docType || undefined },
          { field: "is_indexed", operator: "eq", value: indexed !== undefined ? indexed : undefined },
        ],
        "replace"
      );
      // Reset to page 1 handled by tableProps
    },
    [setFilters]
  );


  const handleDelete = useCallback(
    (id: string, title: string) => {
      deleteDoc(
        {
          resource: "knowledge-base",
          id,
          successNotification: () => ({
            message: `Документ "${title}" удален`,
            description: "",
            type: "success",
          }),
        },
        {
          onSuccess: () => {
            // Simply refetch after deletion, pagination will auto-adjust
            tableQuery.refetch();
          },
        }
      );
    },
    [deleteDoc, tableQuery]
  );

  const handleReindex = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/v1/knowledge-base/reindex`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Ошибка при запуске реиндексации");
      }

      const result = await response.json();
      const jobId = result.job_id;

      if (jobId) {
        registerReindexJob(jobId);
        notify.success("Переиндексация запущена");
      } else {
        throw new Error("Не получен job_id от сервера");
      }
    } catch (error) {
      notify.error(
        "Ошибка при запуске реиндексации",
        error instanceof Error ? error.message : String(error)
      );
    }
  };

  const handleSync = async () => {
    if (syncMarketplaces.length === 0) {
      notify.error("Выберите хотя бы один маркетплейс");
      return;
    }

    setSyncLoading(true);
    const maxItems = syncMode === "quick" ? 100 : 500;
    const results: string[] = [];

    try {
      // Синхронизация WB
      if (syncMarketplaces.includes("wildberries")) {
        const wbResponse = await fetch(`${apiUrl}/api/v1/wb/sync-cards?max_cards=${maxItems}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        if (wbResponse.ok) {
          results.push("Wildberries: синхронизация запущена");
        } else {
          results.push("Wildberries: ошибка запуска");
        }
      }

      // Синхронизация Ozon
      if (syncMarketplaces.includes("ozon")) {
        const ozonResponse = await fetch(`${apiUrl}/api/v1/ozon/sync?max_products=${maxItems}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        if (ozonResponse.ok) {
          results.push("Ozon: синхронизация запущена");
        } else {
          results.push("Ozon: ошибка запуска");
        }
      }

      notify.success(
        "Синхронизация запущена",
        results.join(". ") + ". Обновите страницу через несколько минут."
      );
      setSyncModalVisible(false);

    } catch (error: any) {
      notify.error("Ошибка синхронизации", error.message);
    } finally {
      setSyncLoading(false);
    }
  };

  const renderDocType = (type: string) => {
    const typeMap: Record<string, { label: string; color: string }> = {
      product_info: { label: "Карточка товара", color: "blue" },
      faq: { label: "FAQ", color: "green" },
      tone_of_voice: { label: "Tone of Voice", color: "purple" },
      policy: { label: "Политика", color: "red" },
      instruction: { label: "Инструкция", color: "orange" },
    };

    const config = typeMap[type] || { label: type, color: "default" };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const renderIndexStatus = (isIndexed: boolean) => {
    return isIndexed ? (
      <Tag icon={<CheckCircleOutlined />} color="success">
        Проиндексирован
      </Tag>
    ) : (
      <Tag icon={<ClockCircleOutlined />} color="warning">
        Ожидает
      </Tag>
    );
  };

  const renderMarketplace = (metadata: any) => {
    const mp = metadata?.marketplace;
    if (!mp) return <Text type="secondary">—</Text>;

    const mpConfig: Record<string, { label: string; color: string }> = {
      wildberries: { label: "WB", color: "purple" },
      ozon: { label: "Ozon", color: "blue" },
    };

    const config = mpConfig[mp] || { label: mp, color: "default" };
    return <Badge color={config.color} text={config.label} />;
  };

  const columns: ColumnsType<KBDocument> = [
    {
      title: "Фото",
      dataIndex: ["product_info", "photo_url"],
      width: 70,
      align: "center",
      render: (photoUrl, record) => {
        if (!photoUrl || record.document_type !== "product_info") {
          return <PictureOutlined style={{ fontSize: 24, color: '#d9d9d9' }} />;
        }
        return (
          <Image
            src={photoUrl}
            width={48}
            height={48}
            style={{ objectFit: 'cover', borderRadius: '4px' }}
            fallback="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect width='48' height='48' fill='%23f0f0f0'/%3E%3C/svg%3E"
            preview={false}
          />
        );
      },
    },
    {
      title: "Дата обновления",
      dataIndex: "updated_at",
      width: 110,
      sorter: true,
      defaultSortOrder: "descend",
      render: (date) => dayjs(date).format("YYYY-MM-DD"),
      align: "center"
    },
    {
      title: "Заголовок",
      dataIndex: "title",
      width: 280,
      filteredValue: appliedSearchValue ? [appliedSearchValue] : null,
      filterIcon: <SearchOutlined style={{ color: appliedSearchValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 260 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            allowClear
            placeholder="Поиск по заголовку"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onPressEnter={() => {
              updateFilters(searchDraft, docTypeDraft, indexedDraft);
              close();
            }}
            style={{ marginBottom: 8 }}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, docTypeDraft, indexedDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setSearchDraft("");
              updateFilters("", docTypeDraft, indexedDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: (title, record) => (
        <Space>
          <BookOutlined style={{ color: "#1677ff" }} />
          <Tooltip title={record.content?.substring(0, 200)}>
            <Text strong>{title}</Text>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Категория",
      dataIndex: ["product_info", "category"],
      width: 200,
      render: (category, record) => {
        if (!category || record.document_type !== "product_info") {
          return <Text type="secondary">—</Text>;
        }
        return <Tag color="cyan">{category}</Tag>;
      },
    },
    {
      title: "Маркетплейс",
      dataIndex: "metadata",
      width: 110,
      render: renderMarketplace,
    },
    {
      title: "SKU",
      dataIndex: ["product_info", "seller_sku"],
      width: 110,
      render: (sku) => sku ? <Text code style={{ fontSize: '11px' }}>{sku}</Text> : <Text type="secondary">—</Text>,
    },
    {
      title: "Тип",
      dataIndex: "document_type",
      width: 140,
      filteredValue: appliedDocTypeValue ? [appliedDocTypeValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedDocTypeValue ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 220 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Выберите тип"
            style={{ width: '100%', marginBottom: 8 }}
            value={docTypeDraft}
            onChange={(value) => setDocTypeDraft(value)}
            options={[
              { value: "product_info", label: "Карточка товара" },
              { value: "faq", label: "FAQ" },
              { value: "tone_of_voice", label: "Tone of Voice" },
              { value: "policy", label: "Политика" },
              { value: "instruction", label: "Инструкция" },
            ]}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, docTypeDraft, indexedDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setDocTypeDraft(undefined);
              updateFilters(searchDraft, undefined, indexedDraft);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: renderDocType,
    },
    {
      title: "Характеристики",
      key: "characteristics_count",
      width: 100,
      align: "center",
      render: (_, record) => {
        const mp = record.metadata?.marketplace;
        let count = 0;

        if (mp === "wildberries" && record.product_info?.characteristics) {
          count = Object.keys(record.product_info.characteristics).length;
        } else if (mp === "ozon" && record.product_info?.attributes) {
          count = Object.keys(record.product_info.attributes).length;
        }

        return count > 0 ? (
          <Tag color="geekblue">{count} полей</Tag>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: "Статус",
      dataIndex: "is_indexed",
      width: 150,
      align: "center",
      filteredValue: appliedIndexedValue !== undefined ? [appliedIndexedValue] : null,
      filterIcon: <FilterOutlined style={{ color: appliedIndexedValue !== undefined ? "#1677ff" : undefined }} />,
      filterDropdown: ({ close }) => (
        <div style={{ padding: 8, width: 200 }} onKeyDown={(e) => e.stopPropagation()}>
          <Select
            allowClear
            placeholder="Статус"
            style={{ width: '100%', marginBottom: 8 }}
            value={indexedDraft}
            onChange={(value) => setIndexedDraft(value)}
            options={[
              { value: true, label: "Проиндексирован" },
              { value: false, label: "Ожидает" },
            ]}
          />
          <Space size={8}>
            <Button type="primary" size="small" onClick={() => {
              updateFilters(searchDraft, docTypeDraft, indexedDraft);
              close();
            }}>
              Применить
            </Button>
            <Button size="small" onClick={() => {
              setIndexedDraft(undefined);
              updateFilters(searchDraft, docTypeDraft, undefined);
              close();
            }}>
              Сбросить
            </Button>
          </Space>
        </div>
      ),
      render: renderIndexStatus,
    },
    {
      title: "Действия",
      key: "actions",
      width: 100,
      fixed: "right",
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                go({
                  to: { resource: "knowledge-base", action: "edit", id: record.id },
                  type: "push",
                });
              }}
              aria-label="Редактировать"
            />
          </Tooltip>
          <Popconfirm
            title="Удалить документ"
            description={`Вы уверены, что хотите удалить "${record.title}"?`}
            onConfirm={() => handleDelete(record.id, record.title)}
            okText="Удалить"
            cancelText="Отмена"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Удалить">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                aria-label="Удалить"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <List
        title="База знаний"
        headerButtons={() => (
          <>
            <Button
              icon={<CloudDownloadOutlined />}
              onClick={() => setSyncModalVisible(true)}
            >
              Синхронизация с МП
            </Button>
            <Button
              icon={<SyncOutlined />}
              onClick={handleReindex}
            >
              Переиндексация
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() =>
                go({
                  to: {
                    resource: "knowledge-base",
                    action: "create",
                  },
                  type: "push",
                })
              }
            >
              Создать документ
            </Button>
          </>
        )}
      >
        <Table
          {...tableProps}
          columns={columns}
          rowKey="id"
          bordered
          size="middle"
          scroll={{ x: 1400 }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} документов`,
          }}
        />
      </List>

      {/* Sync Modal */}
      <Modal
        title={
          <Space>
            <ExclamationCircleOutlined style={{ color: '#faad14' }} />
            <span>Синхронизация карточек товаров</span>
          </Space>
        }
        open={syncModalVisible}
        onOk={handleSync}
        onCancel={() => setSyncModalVisible(false)}
        confirmLoading={syncLoading}
        okText="Запустить синхронизацию"
        cancelText="Отмена"
        width={500}
      >
        <Alert
          message="Внимание"
          description="Синхронизация обновит карточки товаров из маркетплейсов. Существующие данные будут обновлены, новые товары добавлены. После синхронизации потребуется переиндексация."
          type="warning"
          showIcon
          style={{ marginBottom: 20 }}
        />

        <div style={{ marginBottom: 20 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Режим синхронизации:</Text>
          <Radio.Group
            value={syncMode}
            onChange={(e) => setSyncMode(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              <Radio value="quick">
                <Text strong>Проверка новых</Text>
                <br />
                <Text type="secondary" style={{ marginLeft: 24 }}>
                  Быстрая проверка последних 100 товаров. Рекомендуется для регулярных обновлений.
                </Text>
              </Radio>
              <Radio value="full">
                <Text strong>Полная синхронизация</Text>
                <br />
                <Text type="secondary" style={{ marginLeft: 24 }}>
                  Загрузка до 500 товаров. Занимает больше времени, рекомендуется для первоначальной загрузки.
                </Text>
              </Radio>
            </Space>
          </Radio.Group>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Маркетплейсы:</Text>
          <Checkbox.Group
            value={syncMarketplaces}
            onChange={(values) => setSyncMarketplaces(values as string[])}
            style={{ width: '100%' }}
          >
            <Space direction="vertical">
              <Checkbox value="wildberries">
                <Badge color="purple" text="Wildberries" />
              </Checkbox>
              <Checkbox value="ozon">
                <Badge color="blue" text="Ozon" />
              </Checkbox>
            </Space>
          </Checkbox.Group>
        </div>
      </Modal>
    </>
  );
}
